/**
 * Remind for a leaver waiting on their supervisor (the row itself opens the leaver). Boris DMs the supervisor (the proxy's
 * `/leavers/:id/ping`); the row then says when, and the button waits a day as the proxy does.
 *
 * A sent reminder is kept HERE as well as on the row's `pingedAt`, and the later one is shown: a
 * list reload that started before the reminder would otherwise bring back the old row and wipe it.
 * The caller keys this by the list's future switch, so a reload starts it afresh (no stale error).
 */
import { type MouseEvent, useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { pingSupervisor } from '../../services/referenceLetterApi';
import type { ReferenceLetterLeaver } from '../../types/referenceLetterDraft';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { nextReminderTime, remindedLabel, remindedRecently, reminderOpensIn } from './reminders';

type Note = { kind: 'error' | 'info'; text: string } | null;

/** Every button in the leaver list's action column: one width, so Draft and Remind line up. */
export const ACTION_BUTTON = 'w-20 justify-center rounded';

/** The later of two ISO times; either may be missing. */
const later = (a: string | null, b: string | null) => (!a ? b : !b ? a : Date.parse(a) >= Date.parse(b) ? a : b);

/** The button and the line under it. */
export function RemindControl({ leaver }: { leaver: ReferenceLetterLeaver }) {
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [note, setNote] = useState<Note>(null);
  const [, setReopened] = useState(0);
  const pingedAt = later(sentAt, leaver.pingedAt);

  // A page left open past the cooldown re-renders when it ends, so Remind comes back by itself
  // (CodeRabbit, PR #561). The 24-hour wait is well inside setTimeout's ~24.8-day limit.
  useEffect(() => {
    const ms = reminderOpensIn(pingedAt);
    if (ms == null) return undefined;
    const timer = setTimeout(() => setReopened((n) => n + 1), ms + 1000);
    return () => clearTimeout(timer);
  }, [pingedAt]);

  // A sleeping laptop delays that timer, and "Reminded today" turns into yesterday at midnight:
  // coming back to the tab re-renders, which re-reads both against the clock.
  useEffect(() => {
    const tick = () => setReopened((n) => n + 1);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, []);

  const noteId = `remind-${leaver.id}`;
  // A greyed-out Remind says when it comes back: "Reminded yesterday" alone would not explain it.
  const waiting = remindedRecently(pingedAt) ? ` · again from ${nextReminderTime(pingedAt as string)}` : '';
  const label = pingedAt ? remindedLabel(pingedAt) : null;
  // "Already reminded today" explains the wait, so it goes when the wait does; an error stays.
  const shown = note?.kind === 'info' && !remindedRecently(pingedAt) ? null : note;
  const line = shown?.text ?? (label ? `${label}${waiting}` : null);

  /** Sends the reminder; a refusal because one went out today counts as reminded, and says so. */
  const remind = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // a reminder must not also open the row
    setSending(true);
    setNote(null);
    try {
      const result = await pingSupervisor(leaver.id);
      setSentAt(result.pingedAt);
      if (result.already) setNote({ kind: 'info', text: 'Already reminded today' });
      void logAnalyticsEvent('reference_letters_supervisor_reminded', { outcome: result.already ? 'already' : 'sent' });
    } catch (err) {
      setNote({ kind: 'error', text: err instanceof Error ? err.message : 'The reminder could not be sent' });
      void logAnalyticsEvent('reference_letters_supervisor_reminded', { outcome: 'failed' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          className={ACTION_BUTTON}
          loading={sending}
          disabled={sending || remindedRecently(pingedAt)}
          aria-describedby={line ? noteId : undefined}
          onClick={remind}
        >
          Remind
        </Button>
      </div>
      {line && (
        <p id={noteId} role="status" className={shown?.kind === 'error' ? 'mt-1 text-xs text-red' : 'mt-1 text-xs text-zinc-600'}>
          {line}
        </p>
      )}
    </>
  );
}
